// The one read of an artifact's version history (#12481): a react-query
// answer the history column reads and every host invalidates after a save,
// a restore or a chat edit landing, rather than a hand-built copy that
// could fall behind (rules/frontend.md, module state rule 2).
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchArtifactVersions,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";

export const artifactVersionsKey = (artifactId: string) =>
  ["artifactVersions", artifactId] as const;

/** The artifact's versions, newest first. `null` while loading; `[]` after
 *  a failed read, which the column reports on its own. */
export const useArtifactVersions = (
  artifactId: string | null | undefined
): { versions: ArtifactVersion[] | null; failed: boolean } => {
  const query = useQuery({
    queryKey: artifactVersionsKey(artifactId ?? ""),
    queryFn: () => fetchArtifactVersions(artifactId ?? ""),
    enabled: Boolean(artifactId),
    retry: false,
  });
  return {
    versions: query.data ?? (query.isError ? [] : null),
    failed: query.isError,
  };
};

/** A save, a restore or a chat edit landing makes the list stale: every
 *  host calls this at that moment, and both readers follow. */
export const useInvalidateArtifactVersions = (): ((
  artifactId: string | null | undefined
) => void) => {
  const queryClient = useQueryClient();
  return useCallback(
    (artifactId) => {
      if (!artifactId) return;
      void queryClient.invalidateQueries({
        queryKey: artifactVersionsKey(artifactId),
      });
    },
    [queryClient]
  );
};
